import { render } from "preact";
import { useEffect, useMemo, useRef, useState } from "preact/hooks";
import type { VNode } from "preact";
import { catalog, catalogById, displayDate, eraOptions, fieldValue, symbolScale, symbolUrl, years } from "./catalog";
import { answerIsPresent, gradeField, isPerfect, limitMatchesForQuery, maskNameHint, rankMatches, rankMatchesByFields, scoreResults, updateFamiliarity, validationErrors } from "./game/engine";
import { chooseNextSet, getScopeCandidates, RECENT_SET_LIMIT } from "./game/selection";
import { FIELDS, type Field, type GameScope, type GameSettings, type Grade, type HistoryEntry, type PersistedStateV1, type RoundAnswers, type RoundFeedback, type SessionRuntime, type SessionStats, type SetRecord } from "./types";
import { defaultSettings } from "./state/defaults";
import { createStorageAdapter, loadState, makeId, resetState, saveState, serializeState, type StorageLike } from "./state/persistence";
import { fieldLabel, fieldMeans, groupedMeans, historyAccuracy, overallMean, percent, trendSnapshots, upsertDailySnapshot } from "./state/summaries";
import "./styles.css";

const app = document.querySelector<HTMLDivElement>("#app");
if (!app) throw new Error("Guess That Set needs an #app element.");

function getStorage(): StorageLike | null {
  try {
    return typeof window === "undefined" ? null : window.localStorage;
  } catch {
    return null;
  }
}

const storage = createStorageAdapter(getStorage());
const loaded = loadState(storage);

type Route = "play" | "progress" | "sets" | "about" | "settings";
type ProgressTab = "overall" | "era" | "year";

interface SubmissionOverrides {
  answers?: RoundAnswers;
  skipped?: ReadonlySet<Field>;
  eraSelection?: string | null;
}

function cloneScope(scope: GameScope): GameScope {
  if (scope.kind === "all") return { kind: "all" };
  return scope.kind === "eras" ? { kind: "eras", eraIds: [...scope.eraIds] } : { kind: "custom", setIds: [...scope.setIds] };
}

function cloneSettings(settings: GameSettings): GameSettings {
  return { fields: [...settings.fields], clues: [...settings.clues], scope: cloneScope(settings.scope), sessionLength: settings.sessionLength, appearance: settings.appearance };
}

function emptyStats(): SessionStats {
  return {
    answered: 0,
    points: 0,
    perfect: 0,
    reviewedIds: [],
    fieldStats: { name: { correct: 0, answered: 0 }, era: { correct: 0, answered: 0 }, year: { correct: 0, answered: 0 } },
  };
}

function initialSession(settings: GameSettings, state: PersistedStateV1, preferredId?: string): SessionRuntime {
  const candidates = getScopeCandidates(settings.scope, catalog);
  const preferred = preferredId ? candidates.find((set) => set.id === preferredId) : undefined;
  const currentSet = preferred ?? chooseNextSet(catalog, settings.scope, settings.fields, state.familiarity);
  return {
    phase: "asking",
    currentSet,
    recentSetIds: currentSet ? [currentSet.id] : [],
    feedback: null,
    stats: emptyStats(),
    answers: {},
    skipped: new Set<Field>(),
    hinted: new Set<Field>(),
    validation: {},
    eraSelection: null,
  };
}

function readRoute(): Route {
  const value = (typeof window === "undefined" ? "" : window.location.hash).replace(/^#\/?/, "");
  const page = value.split("?")[0];
  return page === "progress" || page === "sets" || page === "about" || page === "settings" ? page : "play";
}

function getQuery(): URLSearchParams {
  const hash = typeof window === "undefined" ? "" : window.location.hash;
  return new URLSearchParams(hash.includes("?") ? hash.slice(hash.indexOf("?") + 1) : "");
}

function App(): VNode {
  const [state, setState] = useState<PersistedStateV1>(loaded.state);
  const [draftSettings, setDraftSettings] = useState<GameSettings>(() => cloneSettings(loaded.state.settings));
  const [session, setSession] = useState<SessionRuntime>(() => initialSession(loaded.state.settings, loaded.state));
  const [route, setRoute] = useState<Route>(readRoute());
  const [notice, setNotice] = useState<string | null>(loaded.warning);
  const [menuOpen, setMenuOpen] = useState(false);
  const [progressTab, setProgressTab] = useState<ProgressTab>("overall");
  const [setSearch, setSetSearch] = useState("");
  const [setEraFilter, setSetEraFilter] = useState("");
  const [scopeSearch, setScopeSearch] = useState("");
  const [confirmReset, setConfirmReset] = useState(false);
  const submitLock = useRef(false);

  useEffect(() => {
    const onHashChange = () => {
      setRoute(readRoute());
      setMenuOpen(false);
    };
    const rawPage = window.location.hash.replace(/^#\/?/, "").split("?")[0];
    if (!["play", "progress", "sets", "about", "settings"].includes(rawPage)) window.location.hash = "#/play";
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, []);

  useEffect(() => {
    document.documentElement.dataset.appearance = state.settings.appearance;
  }, [state.settings.appearance]);

  const candidates = useMemo(() => getScopeCandidates(state.settings.scope, catalog), [state.settings.scope]);
  const draftCandidates = useMemo(() => getScopeCandidates(draftSettings.scope, catalog), [draftSettings.scope]);

  function persist(nextState: PersistedStateV1): void {
    const warning = saveState(storage, nextState);
    if (warning) setNotice(warning);
  }

  function startSession(settings: GameSettings, preferredId?: string): boolean {
    const nextCandidates = getScopeCandidates(settings.scope, catalog);
    if (nextCandidates.length === 0) {
      setNotice("Choose at least one playable set before starting.");
      return false;
    }
    const nextState = { ...state, settings: cloneSettings(settings) };
    setState(nextState);
    setDraftSettings(cloneSettings(settings));
    persist(nextState);
    setSession(initialSession(settings, nextState, preferredId));
    setMenuOpen(false);
    setConfirmReset(false);
    return true;
  }

  function updateDraft(mutator: (settings: GameSettings) => void): void {
    setDraftSettings((previous) => {
      const next = cloneSettings(previous);
      mutator(next);
      return next;
    });
  }

  function commitSettings(): void {
    if (startSession(draftSettings)) window.location.hash = "#/play";
  }

  function currentSessionLabel(): string {
    if (state.settings.sessionLength === "endless") return `${session.stats.answered} answered`;
    const current = Math.min(session.stats.answered + (session.phase === "asking" && session.currentSet ? 1 : 0), state.settings.sessionLength);
    return `${current} / ${state.settings.sessionLength}`;
  }

  function submitRound(overrides: SubmissionOverrides = {}): void {
    if (submitLock.current || session.phase !== "asking" || !session.currentSet) return;
    const set = session.currentSet;
    const roundAnswers = overrides.answers ?? session.answers;
    const skipped = overrides.skipped ?? session.skipped;
    const eraSelection = overrides.eraSelection ?? session.eraSelection;
    const errors = validationErrors(state.settings.fields, roundAnswers, skipped, years);
    if (Object.keys(errors).length > 0) {
      setSession((previous) => ({ ...previous, validation: errors }));
      return;
    }
    submitLock.current = true;
    const at = new Date().toISOString();
    const result = {} as Record<Field, Grade>;
    const familiarityBefore: Partial<Record<Field, number>> = {};
    const familiarityAfter: Partial<Record<Field, number>> = {};
    const hintedFields = [...session.hinted];
    const record = { ...(state.familiarity[set.id] ?? { name: 0, era: 0, year: 0, seen: 0, lastSeen: null }) };
    const nextStats = { ...session.stats, fieldStats: { ...session.stats.fieldStats, name: { ...session.stats.fieldStats.name }, era: { ...session.stats.fieldStats.era }, year: { ...session.stats.fieldStats.year } } };
    for (const field of state.settings.fields) {
      familiarityBefore[field] = record[field];
      result[field] = skipped.has(field) ? "skip" : gradeField(field, roundAnswers[field], set, field === "era" ? eraSelection : undefined);
      if (!session.hinted.has(field)) record[field] = updateFamiliarity(record[field], result[field]);
      familiarityAfter[field] = record[field];
      nextStats.fieldStats[field].answered += 1;
      if (result[field] === "correct") nextStats.fieldStats[field].correct += 1;
    }
    record.seen += 1;
    record.lastSeen = at;
    const points = scoreResults(result, hintedFields);
    const perfect = hintedFields.length === 0 && isPerfect(result, state.settings.fields);
    const historyAnswers: Partial<Record<Field, string | number>> = {};
    for (const field of state.settings.fields) {
      const answer = roundAnswers[field];
      if (answer !== undefined) historyAnswers[field] = field === "year" ? Number(answer) : answer;
    }
    const historyEntry: HistoryEntry = {
      id: makeId(),
      setId: set.id,
      asked: [...state.settings.fields],
      clues: [...state.settings.clues],
      answers: historyAnswers,
      result,
      points,
      familiarityAfter,
      at,
    };
    const nextFamiliarity = { ...state.familiarity, [set.id]: record };
    const nextState: PersistedStateV1 = {
      ...state,
      familiarity: nextFamiliarity,
      history: [historyEntry, ...state.history].slice(0, 100),
      totals: { score: state.totals.score + points, streak: perfect ? state.totals.streak + 1 : 0 },
      dailySnapshots: upsertDailySnapshot(state.dailySnapshots, overallMean(catalog, nextFamiliarity)),
    };
    persist(nextState);
    setState(nextState);
    nextStats.answered += 1;
    nextStats.points += points;
    if (perfect) nextStats.perfect += 1;
    if (!nextStats.reviewedIds.includes(set.id)) nextStats.reviewedIds = [...nextStats.reviewedIds, set.id];
    const feedback: RoundFeedback = { set, answers: roundAnswers, result, hinted: hintedFields, points, perfect, familiarityBefore, familiarityAfter, at };
    setSession((previous) => ({ ...previous, phase: "feedback", feedback, stats: nextStats }));
    submitLock.current = false;
  }

  function nextRound(): void {
    const selected = chooseNextSet(catalog, state.settings.scope, state.settings.fields, state.familiarity, session.recentSetIds);
    if (!selected) {
      setSession((previous) => ({ ...previous, phase: "summary", currentSet: null }));
      return;
    }
    setSession((previous) => ({ ...previous, phase: "asking", currentSet: selected, recentSetIds: [...previous.recentSetIds, selected.id].slice(-RECENT_SET_LIMIT), feedback: null, answers: {}, skipped: new Set<Field>(), hinted: new Set<Field>(), validation: {}, eraSelection: null }));
  }

  function hint(): void {
    if (!session.currentSet) return;
    const field = state.settings.fields.find((candidate) => !session.skipped.has(candidate) && !session.hinted.has(candidate) && !answerIsPresent(candidate, session.answers, session.skipped));
    if (!field) return;
    const value = fieldValue(session.currentSet, field);
    setSession((previous) => {
      const answers = { ...previous.answers };
      if (field === "name") delete answers.name;
      else answers[field] = value;
      const skipped = field === "name" ? previous.skipped : new Set([...previous.skipped, field]);
      return {
        ...previous,
        answers,
        skipped,
        hinted: new Set([...previous.hinted, field]),
        validation: { ...previous.validation, [field]: undefined },
        eraSelection: field === "era" ? previous.currentSet?.eraId ?? null : previous.eraSelection,
      };
    });
  }

  function dontKnow(): void {
    if (!session.currentSet || session.phase !== "asking") return;
    submitRound({ answers: {}, skipped: new Set(state.settings.fields), eraSelection: null });
  }

  function resetProgress(): void {
    const reset = resetState(storage);
    setState(reset.state);
    setDraftSettings(cloneSettings(defaultSettings));
    setSession(initialSession(defaultSettings, reset.state));
    setConfirmReset(false);
    setNotice(reset.warning ?? "Local progress reset.");
  }

  function exportProgress(): void {
    const blob = new Blob([serializeState(state)], { type: "application/json;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `guess-that-set-export-${new Date().toISOString().slice(0, 10)}.json`;
    anchor.click();
    URL.revokeObjectURL(url);
    setNotice("A complete copy of your local progress was downloaded.");
  }

  function practiceEra(eraId: string): void {
    const settings: GameSettings = { ...cloneSettings(draftSettings), scope: { kind: "eras", eraIds: [eraId] } };
    if (startSession(settings)) window.location.hash = "#/play";
  }

  return (
    <div className="app-shell">
      <Header route={route} state={state} menuOpen={menuOpen} onMenu={() => setMenuOpen((open) => !open)} />
      {notice && <div className="notice-region" role="status"><p className="notice-copy">{notice}</p><button className="button button-quiet" type="button" onClick={() => setNotice(null)}>Dismiss</button></div>}
      <main className={`app-main page-${route}`}>
        {route === "play" && <PlayPage state={state} session={session} candidates={candidates} currentSessionLabel={currentSessionLabel()} onAnswer={(field, value, eraId) => setSession((previous) => ({ ...previous, answers: { ...previous.answers, [field]: value }, eraSelection: field === "era" ? eraId ?? null : previous.eraSelection, validation: { ...previous.validation, [field]: undefined } }))} onSubmit={submitRound} onHint={hint} onDontKnow={dontKnow} onNext={nextRound} onPracticeAgain={() => startSession(state.settings)} onReview={(id) => window.location.hash = `#/sets?set=${encodeURIComponent(id)}`} onFinish={() => setSession((previous) => ({ ...previous, phase: "summary" }))} onSummaryProgress={() => window.location.hash = "#/progress"} />}
        {route === "progress" && <ProgressPage state={state} tab={progressTab} onTab={setProgressTab} onExport={exportProgress} onReset={() => setConfirmReset(true)} confirmReset={confirmReset} onConfirmReset={resetProgress} onCancelReset={() => setConfirmReset(false)} />}
        {route === "sets" && <SetsPage state={state} search={setSearch} eraFilter={setEraFilter} onSearch={setSetSearch} onEra={setSetEraFilter} onPracticeEra={practiceEra} />}
        {route === "about" && <AboutPage />}
        {route === "settings" && <SettingsPage state={state} draftSettings={draftSettings} scopeSearch={scopeSearch} onScopeSearch={setScopeSearch} onDraft={updateDraft} onStart={commitSettings} onNotice={setNotice} onAppearance={(appearance) => { const next = { ...state, settings: { ...state.settings, appearance } }; setState(next); setDraftSettings((draft) => ({ ...draft, appearance })); persist(next); }} onExport={exportProgress} onReset={() => setConfirmReset(true)} confirmReset={confirmReset} onConfirmReset={resetProgress} onCancelReset={() => setConfirmReset(false)} />}
      </main>
    </div>
  );
}

interface HeaderProps {
  route: Route;
  state: PersistedStateV1;
  menuOpen: boolean;
  onMenu: () => void;
}

function Header({ route, state, menuOpen, onMenu }: HeaderProps): VNode {
  return (
    <header className="app-header">
      <div className="header-inner">
        <a className="brand" href="#/play" aria-label="Guess That Set home"><span className="brand-guess">GUESS</span><span className="brand-that">THAT</span><span className="brand-set">SET</span></a>
        <nav id="primary-navigation" className={`primary-nav${menuOpen ? " is-open" : ""}`} aria-label="Primary navigation">
          {(["play", "progress", "sets", "about", "settings"] as Route[]).map((item) => <a key={item} className={`nav-link${route === item ? " is-active" : ""}`} href={`#/${item}`} aria-current={route === item ? "page" : undefined}>{item === "settings" ? "Config" : item[0].toLocaleUpperCase() + item.slice(1)}</a>)}
        </nav>
        <div className="header-metrics"><div className="header-metric"><span className="metric-icon" aria-hidden="true">♨</span><strong>{state.totals.streak}</strong><span>streak</span></div><div className="header-metric"><span className="metric-icon" aria-hidden="true">▥</span><strong>{state.totals.score.toLocaleString()}</strong><span>score</span></div></div>
        <button className="menu-button" type="button" aria-label={menuOpen ? "Close navigation menu" : "Open navigation menu"} aria-expanded={menuOpen} aria-controls="primary-navigation" onClick={onMenu}><span aria-hidden="true">{menuOpen ? "×" : "☰"}</span></button>
      </div>
    </header>
  );
}

interface SettingsCardProps {
  draftSettings: GameSettings;
  candidateCount: number;
  scopeSearch: string;
  onScopeSearch: (value: string) => void;
  onDraft: (mutator: (settings: GameSettings) => void) => void;
  onStart: () => void;
  onNotice?: (message: string) => void;
}

function SettingsCard({ draftSettings, candidateCount, scopeSearch, onScopeSearch, onDraft, onStart, onNotice }: SettingsCardProps): VNode {
  function toggleField(field: Field): void {
    onDraft((settings) => {
      if (settings.fields.includes(field)) {
        if (settings.fields.length > 1) settings.fields = settings.fields.filter((item) => item !== field);
        else onNotice?.("Keep at least one answer field selected.");
      } else settings.fields.push(field);
      settings.clues = settings.clues.filter((item) => !settings.fields.includes(item));
    });
  }

  function toggleClue(field: Field): void {
    onDraft((settings) => {
      if (settings.fields.includes(field)) {
        if (settings.fields.length === 1) return;
        settings.fields = settings.fields.filter((item) => item !== field);
        if (!settings.clues.includes(field)) settings.clues.push(field);
      } else if (settings.clues.includes(field)) settings.clues = settings.clues.filter((item) => item !== field);
      else settings.clues.push(field);
    });
  }

  function clearPointerFocus(event: { currentTarget: EventTarget | null }): void {
    const target = event.currentTarget;
    if (!(target instanceof HTMLElement)) return;
    target.blur();
    window.requestAnimationFrame(() => target.blur());
  }

  function clearPointerClickFocus(event: MouseEvent): void {
    if (event.detail > 0) clearPointerFocus(event);
  }

  function chooseScope(kind: "all" | "eras" | "custom"): void {
    onDraft((settings) => {
      if (kind === "all") settings.scope = { kind: "all" };
      else if (kind === "eras") settings.scope = { kind: "eras", eraIds: settings.scope.kind === "eras" && settings.scope.eraIds.length ? settings.scope.eraIds : [eraOptions[0]?.id ?? ""] };
      else settings.scope = { kind: "custom", setIds: settings.scope.kind === "custom" ? settings.scope.setIds : [] };
    });
  }

  function toggleEra(id: string): void {
    onDraft((settings) => {
      const ids = settings.scope.kind === "eras" ? [...settings.scope.eraIds] : [];
      settings.scope = { kind: "eras", eraIds: ids.includes(id) ? ids.filter((item) => item !== id) : [...ids, id] };
    });
  }

  function addCustomSet(set: SetRecord): void {
    onDraft((settings) => {
      if (settings.scope.kind !== "custom") settings.scope = { kind: "custom", setIds: [set.id] };
      else if (!settings.scope.setIds.includes(set.id)) settings.scope.setIds = [...settings.scope.setIds, set.id];
    });
    onScopeSearch("");
  }

  function removeCustomSet(id: string): void {
    onDraft((settings) => {
      if (settings.scope.kind === "custom") settings.scope.setIds = settings.scope.setIds.filter((item) => item !== id);
    });
  }

  const customMatches = rankMatchesByFields(catalog, scopeSearch, (set) => [set.name, set.id, set.era, String(set.year)]);
  const customVisible = limitMatchesForQuery(customMatches, scopeSearch);
  const customOptions = customVisible.matches;
  const hasMore = customVisible.hasMore;
  return (
    <article className="card settings-card">
      <div className="panel-header"><div><p className="eyebrow">Config</p><h2>Quiz configuration</h2></div></div>
      <fieldset className="settings-fieldset"><legend className="setting-legend">What do you need to guess?</legend><div className="field-choices">
        {FIELDS.map((field) => <button key={field} className={`field-choice choice-${field}${draftSettings.fields.includes(field) ? " is-selected" : ""}`} type="button" aria-pressed={draftSettings.fields.includes(field)} onPointerUp={clearPointerFocus} onClick={(event) => { toggleField(field); clearPointerClickFocus(event); }}><span>{fieldLabel(field)}</span><span className="choice-check" aria-hidden="true">{draftSettings.fields.includes(field) ? "✓" : ""}</span></button>)}
      </div></fieldset>
      <fieldset className="settings-fieldset clues-fieldset"><legend className="setting-legend">What’s revealed?</legend><div className="clue-choices">
        <button className={`clue-choice${draftSettings.clues.length === 0 ? " is-selected" : ""}`} type="button" aria-pressed={draftSettings.clues.length === 0} onPointerUp={clearPointerFocus} onClick={(event) => { onDraft((settings) => { settings.clues = []; }); clearPointerClickFocus(event); }}><span className="choice-leading">◌</span><span><strong>Nothing</strong><small>Just the symbol</small></span></button>
        {FIELDS.map((field) => { const selected = draftSettings.clues.includes(field); const locked = draftSettings.fields.length === 1 && draftSettings.fields.includes(field); return <button key={field} className={`clue-choice clue-choice-${field}${selected ? " is-selected" : ""}`} type="button" aria-pressed={selected} disabled={locked} onPointerUp={clearPointerFocus} onClick={(event) => { toggleClue(field); clearPointerClickFocus(event); }}><span className="choice-leading">{field === "year" ? "▣" : field === "era" ? "◫" : "▤"}</span><span><strong>{fieldLabel(field)}</strong><small>{selected ? `${fieldLabel(field)} is shown` : `Reveal ${fieldLabel(field).toLocaleLowerCase()}`}</small></span></button>; })}
      </div></fieldset>
      <fieldset className="settings-fieldset scope-fieldset"><legend className="setting-legend">Set scope</legend><div className="scope-options">
        {(["all", "eras", "custom"] as const).map((kind) => <label key={kind} className="radio-option"><input type="radio" name="scope-kind" value={kind} checked={draftSettings.scope.kind === kind} onChange={() => chooseScope(kind)} />{kind === "all" ? "All sets" : kind === "eras" ? "Choose eras" : "Choose sets"}</label>)}
      </div>
      {draftSettings.scope.kind === "eras" && <div className="scope-era-grid">{eraOptions.map((era) => <label key={era.id} className="scope-era-option"><input type="checkbox" checked={draftSettings.scope.kind === "eras" && draftSettings.scope.eraIds.includes(era.id)} onChange={() => toggleEra(era.id)} />{era.name} <small>({era.count})</small></label>)}</div>}
      {draftSettings.scope.kind === "custom" && <div className="scope-custom-picker"><label className="answer-label" htmlFor="scope-search">Find a set to add</label><Combobox id="scope-search" value={scopeSearch} options={customOptions.map(({ item }) => ({ id: item.id, label: `${item.name} · ${item.id}`, item }))} placeholder="Search by set name or ID" onChange={onScopeSearch} onSelect={(option) => addCustomSet(option.item)} />{hasMore && <small className="suggestion-more">More matches available — keep typing to narrow.</small>}<div className="selected-sets">{draftSettings.scope.setIds.length === 0 ? <p className="muted-copy">No sets selected yet.</p> : draftSettings.scope.setIds.map((id) => { const set = catalogById.get(id); return set ? <span className="selected-set-pill" key={id}>{set.name}<button type="button" className="pill-remove" aria-label={`Remove ${set.name}`} onClick={() => removeCustomSet(id)}>×</button></span> : null; })}</div></div>}
      </fieldset>
      <fieldset className="settings-fieldset session-fieldset"><legend className="setting-legend">Session length</legend><div className="session-options">{([5, 10, 20, "endless"] as const).map((length) => <label key={String(length)} className="session-option"><input type="radio" name="session-length" value={String(length)} checked={draftSettings.sessionLength === length} onChange={() => onDraft((settings) => { settings.sessionLength = length; })} />{length === "endless" ? "Endless" : `${length} rounds`}</label>)}</div></fieldset>
      <p className="settings-status" id="settings-status" role="status">{candidateCount ? `${candidateCount} set${candidateCount === 1 ? "" : "s"} in this practice pool.` : "This scope has no playable sets."}</p>
      <button className="button button-primary start-button" type="button" disabled={candidateCount === 0} onClick={onStart}>Start game</button>
    </article>
  );
}

interface ComboOption<T = unknown> { id: string; label: string; item: T; }

interface ComboboxProps<T> {
  id: string;
  value: string;
  options: ComboOption<T>[];
  placeholder: string;
  onChange: (value: string) => void;
  onSelect: (option: ComboOption<T>) => void;
  disabled?: boolean;
  describedBy?: string;
}

function Combobox<T>({ id, value, options, placeholder, onChange, onSelect, disabled, describedBy }: ComboboxProps<T>): VNode {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const comboboxRef = useRef<HTMLDivElement>(null);
  const matches = limitMatchesForQuery(rankMatches(options, value, (option) => option.label), value);
  useEffect(() => {
    if (!open) return;
    const closeIfOutside = (event: Event) => {
      const target = event.target;
      if (target instanceof Node && !comboboxRef.current?.contains(target)) {
        setOpen(false);
        setActive(-1);
      }
    };
    document.addEventListener("pointerdown", closeIfOutside);
    document.addEventListener("focusin", closeIfOutside);
    return () => {
      document.removeEventListener("pointerdown", closeIfOutside);
      document.removeEventListener("focusin", closeIfOutside);
    };
  }, [open]);
  function choose(option: ComboOption<T>): void { onSelect(option); setOpen(false); setActive(-1); }
  function onKeyDown(event: KeyboardEvent): void {
    if (event.key === "ArrowDown") { event.preventDefault(); setOpen(true); setActive((current) => Math.min(current + 1, matches.matches.length - 1)); }
    else if (event.key === "ArrowUp") { event.preventDefault(); setOpen(true); setActive((current) => Math.max(current - 1, 0)); }
    else if (event.key === "Escape") { setOpen(false); setActive(-1); }
    else if (event.key === "Enter" && open && active >= 0 && matches.matches[active]) { event.preventDefault(); choose(matches.matches[active].item); }
  }
  const activeId = active >= 0 && matches.matches[active] ? `${id}-option-${matches.matches[active].item.id}` : undefined;
  return <div ref={comboboxRef} className="combobox"><input id={id} className="answer-input" type="text" role="combobox" aria-autocomplete="list" aria-expanded={open} aria-controls={`${id}-suggestions`} aria-activedescendant={activeId} aria-describedby={describedBy} autoComplete="off" autoCorrect="off" autoCapitalize="none" spellcheck={false} disabled={disabled} placeholder={placeholder} value={value} onInput={(event) => { onChange((event.currentTarget as HTMLInputElement).value); setOpen(true); setActive(-1); }} onClick={() => setOpen(true)} onKeyDown={onKeyDown} /><ul id={`${id}-suggestions`} className="suggestions" role="listbox" hidden={!open || matches.matches.length === 0}>{matches.matches.map((match, index) => <li key={match.item.id} id={`${id}-option-${match.item.id}`} className={index === active ? "is-active" : undefined} role="option" aria-selected={index === active} onMouseDown={(event) => event.preventDefault()} onClick={() => choose(match.item)}>{match.item.label}</li>)}{matches.hasMore && <li className="suggestion-more" role="status">More matches available…</li>}</ul></div>;
}

interface PickerOption {
  id: string;
  label: string;
}

interface ModalPickerProps {
  id: string;
  label: string;
  value: string;
  selectedId?: string | null;
  options: PickerOption[];
  placeholder: string;
  onSelect: (option: PickerOption) => void;
  disabled?: boolean;
  describedBy?: string;
}

function ModalPicker({ id, label, value, selectedId, options, placeholder, onSelect, disabled, describedBy }: ModalPickerProps): VNode {
  const [open, setOpen] = useState(false);
  const [restoreFocus, setRestoreFocus] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dialogRef = useRef<HTMLElement>(null);
  const titleId = `${id}-dialog-title`;

  useEffect(() => {
    if (!open) {
      if (restoreFocus) {
        triggerRef.current?.focus();
        setRestoreFocus(false);
      }
      return;
    }

    const focusFrame = window.requestAnimationFrame(() => {
      const selected = dialogRef.current?.querySelector<HTMLButtonElement>("[aria-pressed=\"true\"]");
      const first = dialogRef.current?.querySelector<HTMLButtonElement>("button:not([disabled])");
      (selected ?? first)?.focus();
    });
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        setOpen(false);
        setRestoreFocus(true);
        return;
      }
      if (event.key !== "Tab") return;
      const focusable = Array.from(dialogRef.current?.querySelectorAll<HTMLElement>("button:not([disabled])") ?? []);
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      window.cancelAnimationFrame(focusFrame);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open, restoreFocus]);

  function choose(option: PickerOption): void {
    onSelect(option);
    setOpen(false);
    setRestoreFocus(true);
  }

  function close(): void {
    setOpen(false);
    setRestoreFocus(true);
  }

  return <div className="modal-picker">
    <button ref={triggerRef} id={id} className="answer-input selector-trigger" type="button" aria-haspopup="dialog" aria-expanded={open} aria-describedby={describedBy} disabled={disabled} onClick={() => { setOpen(true); setRestoreFocus(true); }}>
      <span className={value ? undefined : "selector-placeholder"}>{value || placeholder}</span><span className="selector-caret" aria-hidden="true">⌄</span>
    </button>
    {open && <div className="selector-modal-backdrop" onClick={(event) => { if (event.target === event.currentTarget) close(); }}>
      <section ref={dialogRef} className={`selector-modal selector-modal-${id}`} role="dialog" aria-modal="true" aria-labelledby={titleId} aria-describedby={describedBy}>
        <div className="selector-modal-header"><h2 id={titleId}>{label}</h2><button className="selector-modal-close" type="button" aria-label={`Close ${label} selector`} onClick={close}>×</button></div>
        <div className="selector-options" role="listbox" aria-label={label}>
          {options.length === 0 ? <p className="selector-empty">No options available.</p> : options.map((option) => <button key={option.id} className={`selector-option${selectedId === option.id ? " is-selected" : ""}`} type="button" role="option" aria-selected={selectedId === option.id} aria-pressed={selectedId === option.id} onClick={() => choose(option)}>{option.label}</button>)}
        </div>
      </section>
    </div>}
  </div>;
}

interface SymbolImageProps {
  set: SetRecord;
  asking: boolean;
  className?: string;
}

function SymbolImage({ set, asking, className = "symbol-art" }: SymbolImageProps): VNode {
  const [broken, setBroken] = useState(false);
  useEffect(() => setBroken(false), [set.id]);
  if (broken) return <div className="symbol-fallback" role="img" aria-label={asking ? "Unknown Pokémon TCG set symbol unavailable" : `${set.name} symbol unavailable`}>Symbol unavailable</div>;
  return <img className={className} src={symbolUrl(set)} alt={asking ? "Unknown Pokémon TCG set symbol" : `${set.name} set symbol`} loading="lazy" onError={() => setBroken(true)} style={import.meta.env.DEV ? { transform: `scale(${symbolScale(set)})` } : undefined} />;
}

function SetSymbolBackground(): VNode {
  return <div className="symbol-background" aria-hidden="true" />;
}

interface PlayPageProps {
  state: PersistedStateV1;
  session: SessionRuntime;
  candidates: SetRecord[];
  currentSessionLabel: string;
  onAnswer: (field: Field, value: string, eraId?: string | null) => void;
  onSubmit: () => void;
  onHint: () => void;
  onDontKnow: () => void;
  onNext: () => void;
  onPracticeAgain: () => void;
  onReview: (setId: string) => void;
  onFinish: () => void;
  onSummaryProgress: () => void;
}

function PlayPage(props: PlayPageProps): VNode {
  const { state, session, candidates, currentSessionLabel, onAnswer, onSubmit, onHint, onDontKnow, onNext, onPracticeAgain, onReview, onFinish, onSummaryProgress } = props;
  return <div className="dashboard">
    <div className="primary-column">
      {session.phase === "asking" && <QuizCard state={state} session={session} candidates={candidates} currentSessionLabel={currentSessionLabel} onAnswer={onAnswer} onSubmit={onSubmit} onHint={onHint} onDontKnow={onDontKnow} />}
      {session.phase === "feedback" && session.feedback && <FeedbackCard state={state} session={session} onNext={onNext} onReview={onReview} onFinish={onFinish} />}
      {session.phase === "summary" && <SessionSummary session={session} onPracticeAgain={onPracticeAgain} onSummaryProgress={onSummaryProgress} />}
      {session.phase !== "summary" && <RecentGuesses state={state} />}
      <HowItWorks />
    </div>
    <aside className="sidebar-column">
      <FamiliarityCard state={state} tab="overall" />
    </aside>
  </div>;
}

interface QuizProps {
  state: PersistedStateV1;
  session: SessionRuntime;
  candidates: SetRecord[];
  currentSessionLabel: string;
  onAnswer: (field: Field, value: string, eraId?: string | null) => void;
  onSubmit: () => void;
  onHint: () => void;
  onDontKnow: () => void;
}

function QuizCard({ state, session, candidates, currentSessionLabel, onAnswer, onSubmit, onHint, onDontKnow }: QuizProps): VNode {
  const set = session.currentSet;
  if (!set) return <article className="card quiz-card"><p className="empty-copy">There are no playable sets in this scope.</p></article>;
  const hintEligible = state.settings.fields.find((field) => !session.skipped.has(field) && !answerIsPresent(field, session.answers, session.skipped));
  const canSubmit = state.settings.fields.every((field) => answerIsPresent(field, session.answers, session.skipped));
  return <article className="card quiz-card" aria-label="Guess the set round">
    <div className="question-symbol-panel"><span className="round-count" aria-live="polite">{currentSessionLabel}</span><SetSymbolBackground /><div className="symbol-image-wrap"><SymbolImage set={set} asking className="question-symbol" /></div><div className="symbol-dots" aria-label="Fields to answer">{state.settings.fields.map((field, index) => <span className={`symbol-dot${index === 0 ? " is-current" : ""}`} key={field} aria-label={`${fieldLabel(field)} answer ${index + 1}`} />)}</div></div>
    {state.settings.clues.length > 0 && <div className="clue-strip"><span className="clue-title">Clue</span>{state.settings.clues.map((field) => <span className={`clue-pill clue-${field}`} key={field}><strong>{fieldLabel(field)}:</strong> {fieldValue(set, field)}</span>)}</div>}
    <form className="answer-form" onSubmit={(event) => { event.preventDefault(); onSubmit(); }} noValidate>
      {state.settings.fields.map((field) => <AnswerControl key={field} field={field} session={session} candidates={candidates} onAnswer={onAnswer} />)}
      <div className="answer-actions"><button className="button button-primary button-large" type="submit" disabled={!canSubmit}>Submit guess</button><button className="button button-secondary" type="button" disabled={!hintEligible} onClick={onHint}>♧ <span>Hint</span></button><button className="button button-quiet dont-know-button" type="button" onClick={onDontKnow}>I don't know</button></div>
    </form>
  </article>;
}

interface AnswerControlProps {
  field: Field;
  session: SessionRuntime;
  candidates: SetRecord[];
  onAnswer: (field: Field, value: string, eraId?: string | null) => void;
}

function AnswerControl({ field, session, candidates, onAnswer }: AnswerControlProps): VNode {
  const value = session.answers[field] ?? "";
  const error = session.validation[field];
  const hinted = session.hinted.has(field);
  const disabled = session.skipped.has(field);
  const describedBy = error ? `${field}-error` : undefined;
  const groupClass = `answer-control answer-${field}`;
  const label = <label className="answer-label" htmlFor={`answer-${field}`}>{fieldLabel(field)}</label>;
  if (field === "name") {
    const options = catalog.map((set) => ({ id: set.id, label: set.name, item: set }));
    return <div className={groupClass} data-answer-field={field}>{label}<Combobox id="answer-name" value={String(value)} options={options} placeholder="Type to search set names" disabled={disabled} describedBy={describedBy} onChange={(next) => onAnswer(field, next)} onSelect={(option) => onAnswer(field, option.item.name)} />{error && <FieldError field={field} message={error} />}{hinted && session.currentSet && <AnswerHint field={field} set={session.currentSet} />}</div>;
  }
  if (field === "era") {
    const options = eraOptions.map((era) => ({ id: era.id, label: era.name }));
    return <div className={groupClass} data-answer-field={field}>{label}<ModalPicker id="answer-era" label="Era" value={String(value)} selectedId={session.eraSelection} options={options} placeholder="Select era" disabled={disabled} describedBy={describedBy} onSelect={(option) => onAnswer(field, option.label, option.id)} />{error && <FieldError field={field} message={error} />}{hinted && session.currentSet && <AnswerHint field={field} set={session.currentSet} />}</div>;
  }
  const options = years.map((year) => ({ id: String(year), label: String(year) }));
  return <div className={groupClass} data-answer-field={field}>{label}<ModalPicker id="answer-year" label="Release year" value={String(value)} selectedId={String(value)} options={options} placeholder="Select release year" disabled={disabled} describedBy={describedBy} onSelect={(option) => onAnswer(field, option.label)} />{error && <FieldError field={field} message={error} />}{hinted && session.currentSet && <AnswerHint field={field} set={session.currentSet} />}</div>;
}

function AnswerHint({ field, set }: { field: Field; set: SetRecord }): VNode {
  const value = field === "name" ? maskNameHint(set.name) : fieldValue(set, field);
  return <p className="hint-revealed">{field === "name" ? "Hint" : "Revealed"}: {value}</p>;
}

function FieldError({ field, message }: { field: Field; message: string }): VNode {
  return <p className="field-error" id={`${field}-error`} role="alert">{message}</p>;
}

function feedbackSummary(feedback: RoundFeedback): { label: string; icon: string; tone: string } {
  const grades = Object.values(feedback.result);
  if (feedback.perfect) return { label: "Correct!", icon: "✓", tone: "success" };
  if (feedback.hinted.length > 0 && grades.length > 0 && grades.every((grade) => grade === "correct")) return { label: "Correct with hint", icon: "✓", tone: "success" };
  if (grades.every((grade) => grade === "skip")) return { label: "Revealed", icon: "i", tone: "info" };
  if (grades.includes("incorrect")) return { label: "Not quite", icon: "×", tone: "error" };
  if (grades.some((grade) => grade === "correct" || grade === "close")) return { label: "Almost", icon: "~", tone: "warning" };
  return { label: "Revealed", icon: "i", tone: "info" };
}

interface FeedbackProps {
  state: PersistedStateV1;
  session: SessionRuntime;
  onNext: () => void;
  onReview: (setId: string) => void;
  onFinish: () => void;
}

function FeedbackCard({ state, session, onNext, onReview, onFinish }: FeedbackProps): VNode {
  const feedback = session.feedback;
  const headingRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => { if (feedback) headingRef.current?.focus(); }, [feedback?.set.id]);
  if (!feedback) return <article className="card" />;
  const summary = feedbackSummary(feedback);
  const atLimit = state.settings.sessionLength !== "endless" && session.stats.answered >= state.settings.sessionLength;
  return <article className={`card feedback-card feedback-${summary.tone}`}>
    <div className="feedback-summary" role="status" aria-live="polite"><span className="feedback-icon" aria-hidden="true">{summary.icon}</span><h1 ref={headingRef} className="feedback-heading" tabIndex={-1} data-feedback-heading>{summary.label}</h1></div>
    <div className="feedback-identity"><div className="feedback-identity-heading"><h2 className="feedback-set-name">{feedback.set.name}</h2><span className="feedback-release-year"><span className="labeled-value-label">Release year</span><strong>{feedback.set.year}</strong></span></div><div className="feedback-symbol"><SetSymbolBackground /><SymbolImage set={feedback.set} asking={false} className="feedback-symbol-art" /></div><div className="feedback-metadata"><div className="feedback-values"><span className="labeled-value accent-era"><span className="labeled-value-label">Era</span><strong className="labeled-value-value">{feedback.set.era}</strong></span><span className="labeled-value accent-year"><span className="labeled-value-label">Release date</span><strong className="labeled-value-value">{displayDate(feedback.set)}</strong></span></div></div></div>
    <div className="result-rows">{state.settings.fields.map((field) => <ResultRow key={field} field={field} feedback={feedback} />)}</div>
    <div className="points-line"><span aria-hidden="true">★</span><strong>+{feedback.points} points</strong></div>
    <div className="delta-line"><strong>Familiarity</strong>{state.settings.fields.map((field) => { const before = feedback.familiarityBefore[field] ?? 0; const after = feedback.familiarityAfter[field] ?? before; const delta = Math.round((after - before) * 100); return <span className={`delta-pill ${delta >= 0 ? "delta-positive" : "delta-negative"}`} key={field}>{fieldLabel(field)} {delta >= 0 ? "+" : ""}{delta}%</span>; })}</div>
    <div className="feedback-actions"><button className="button button-primary button-large" type="button" onClick={atLimit ? onFinish : onNext}>{atLimit ? "View summary" : "Next set"}</button><button className="button button-secondary" type="button" onClick={() => onReview(feedback.set.id)}>Review set</button><button className="button button-quiet" type="button" onClick={onFinish}>Finish session</button></div>
  </article>;
}

function ResultRow({ field, feedback }: { field: Field; feedback: RoundFeedback }): VNode {
  const grade = feedback.result[field];
  const status = grade === "skip" ? "Revealed" : grade[0].toLocaleUpperCase() + grade.slice(1);
  const icon = grade === "correct" ? "✓" : grade === "close" ? "~" : grade === "skip" ? "i" : "×";
  const answer = feedback.answers[field];
  const answerText = answer === undefined || String(answer).trim() === "" ? "No answer" : String(answer);
  const resultText = grade === "correct" ? answerText : `${answerText} · Answer: ${fieldValue(feedback.set, field)}`;
  return <div className={`result-row result-${field} result-${grade}`}><span className="result-status"><span className="result-icon" aria-hidden="true">{icon}</span>{status}</span><div className="result-copy"><strong>{fieldLabel(field)}</strong><p>{resultText}</p></div></div>;
}

function SessionSummary({ session, onPracticeAgain, onSummaryProgress }: { session: SessionRuntime; onPracticeAgain: () => void; onSummaryProgress: () => void }): VNode {
  const headingRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => { headingRef.current?.focus(); }, []);
  return <article className="card session-summary"><p className="eyebrow">Nice work</p><h1 ref={headingRef} className="summary-heading" tabIndex={-1} data-summary-heading>Session complete</h1><p className="summary-intro">Your lifetime progress is already saved locally.</p><div className="summary-stats"><LabeledValue label="Rounds" value={String(session.stats.answered)} /><LabeledValue label="Session points" value={session.stats.points.toLocaleString()} accent="score" /><LabeledValue label="Perfect rounds" value={String(session.stats.perfect)} accent="success" /><LabeledValue label="Sets reviewed" value={String(session.stats.reviewedIds.length)} /></div><section className="session-accuracy"><h2>Field accuracy</h2><div className="accuracy-grid">{FIELDS.map((field) => { const values = session.stats.fieldStats[field]; return <div className="accuracy-item" key={field}><span>{fieldLabel(field)}</span><strong>{values.answered ? Math.round(values.correct / values.answered * 100) : 0}%</strong><small>{values.correct} correct / {values.answered} asked</small></div>; })}</div></section><div className="summary-actions"><button className="button button-primary" type="button" onClick={onPracticeAgain}>Practice again</button><button className="button button-quiet" type="button" onClick={onSummaryProgress}>View progress</button></div></article>;
}

function LabeledValue({ label, value, accent }: { label: string; value: string; accent?: string }): VNode {
  return <span className={`labeled-value${accent ? ` accent-${accent}` : ""}`}><span className="labeled-value-label">{label}</span><strong className="labeled-value-value">{value}</strong></span>;
}

function RecentGuesses({ state }: { state: PersistedStateV1 }): VNode {
  return <article className="card recent-card"><div className="panel-header"><div><h2>Recent guesses</h2></div><a className="text-link" href="#/progress">View all</a></div>{state.history.length === 0 ? <p className="empty-copy">Your answered rounds will appear here.</p> : <HistoryTable entries={state.history.slice(0, 5)} />}</article>;
}

function HistoryTable({ entries }: { entries: HistoryEntry[] }): VNode {
  return <div className="table-wrap"><table className="history-table"><thead><tr><th>Set</th><th>Era</th><th>Year</th><th>Score</th></tr></thead><tbody>{entries.map((entry) => <HistoryRow key={entry.id} entry={entry} />)}</tbody></table></div>;
}

function HistoryRow({ entry }: { entry: HistoryEntry }): VNode {
  const set = catalogById.get(entry.setId);
  const grades = Object.values(entry.result);
  const incorrect = grades.includes("incorrect");
  const result = grades.every((grade) => grade === "correct") ? "✓" : incorrect ? "×" : "~";
  return <tr><td><span className={`history-status history-${incorrect ? "bad" : "good"}`} aria-label={incorrect ? "Incorrect" : "Submitted"}>{result}</span>{set?.name ?? entry.setId}</td><td>{set?.era ?? "—"}</td><td>{set?.year ?? "—"}</td><td className="history-score">{entry.points ? `+${entry.points}` : "0"}</td></tr>;
}

function HowItWorks(): VNode {
  const steps = [["◉", "See the symbol", "A random local set symbol is shown."], ["▤", "Make your guess", "Guess the name, era, and year."], ["✓ ×", "Get feedback", "Earn points and improve familiarity."], ["▥", "Track progress", "Weak sets return more often."]];
  return <section className="card how-it-works"><h2>How it works</h2><div className="how-steps">{steps.map(([icon, title, description]) => <div className="how-step" key={title}><div className="how-icon" aria-hidden="true">{icon}</div><strong>{title}</strong><p>{description}</p></div>)}</div></section>;
}

interface FamiliarityProps {
  state: PersistedStateV1;
  tab: ProgressTab;
  onTab?: (tab: ProgressTab) => void;
  large?: boolean;
}

function FamiliarityCard({ state, tab, onTab, large = false }: FamiliarityProps): VNode {
  const [localTab, setLocalTab] = useState<ProgressTab>(tab);
  const activeTab = onTab ? tab : localTab;
  const means = fieldMeans(catalog, state.familiarity);
  const overall = overallMean(catalog, state.familiarity);
  const groups = activeTab === "era" ? groupedMeans(catalog, state.familiarity, "era") : activeTab === "year" ? groupedMeans(catalog, state.familiarity, "year") : [];
  const snapshots = trendSnapshots(state);
  return <article className={`card familiarity-card${large ? " familiarity-large" : ""}`}>
    <div className="panel-header"><div><p className="eyebrow">Your familiarity</p><h2>{large ? "Progress overview" : "Familiarity"}</h2></div><span className="help-mark" title="Unseen sets count as zero">?</span></div>
    <div className="progress-tabs" role="tablist" aria-label="Familiarity grouping">{(["overall", "era", "year"] as const).map((item) => <button key={item} type="button" role="tab" aria-selected={activeTab === item} className={activeTab === item ? "is-selected" : undefined} onClick={() => onTab ? onTab(item) : setLocalTab(item)}>{item === "overall" ? "Overall" : item === "era" ? "By Era" : "By Year"}</button>)}</div>
    {activeTab === "overall" && <><div className="familiarity-overview"><div className="familiarity-ring" style={{ background: `conic-gradient(var(--success) ${overall * 360}deg, var(--ring-track) 0deg)` }}><div className="ring-center"><strong>{percent(overall)}%</strong><span>Overall familiarity</span><small>{overall > 0.5 ? "Good job!" : "Keep practicing"}</small></div></div><div className="field-score-list">{FIELDS.map((field) => <div className={`field-score field-score-${field}`} key={field}><span className="field-score-dot" aria-hidden="true" /><span>{fieldLabel(field)}</span><strong>{percent(means[field])}%</strong></div>)}</div></div><div className="trend-section"><div className="trend-heading"><h3>Recent trend</h3><strong>{trendDelta(snapshots)}</strong></div><TrendChart snapshots={snapshots} /></div></>}
    {activeTab !== "overall" && <div className="group-summary"><p className="muted-copy">Mean familiarity across all three fields. Unseen sets are included.</p>{groups.length === 0 ? <p className="empty-copy">No grouped data yet.</p> : <div className="group-list">{groups.map((group) => <div className="group-row" key={group.key}><div><strong>{group.label}</strong><small>{group.count} set{group.count === 1 ? "" : "s"}</small></div><div className="group-bar"><span style={{ width: `${percent(group.overall)}%` }} /></div><strong>{percent(group.overall)}%</strong></div>)}</div>}</div>}
    <div className="familiarity-foot"><span>{catalog.filter((set) => (state.familiarity[set.id]?.seen ?? 0) > 0).length} of {catalog.length} sets seen</span><span>{state.history.length} recent answers</span></div>
  </article>;
}

function trendDelta(snapshots: PersistedStateV1["dailySnapshots"]): string {
  if (snapshots.length < 2) return snapshots.length === 1 ? `${percent(snapshots[0].overall)}% today` : "No trend yet";
  const delta = Math.round((snapshots[snapshots.length - 1].overall - snapshots[0].overall) * 100);
  return `${delta >= 0 ? "+" : ""}${delta}%`;
}

function TrendChart({ snapshots }: { snapshots: PersistedStateV1["dailySnapshots"] }): VNode {
  if (snapshots.length < 2) return <p className="chart-empty">Answer a second day to see your trend.</p>;
  const min = Math.min(...snapshots.map((snapshot) => snapshot.overall));
  const max = Math.max(...snapshots.map((snapshot) => snapshot.overall));
  const range = Math.max(0.08, max - min);
  const points = snapshots.map((snapshot, index) => `${(index / (snapshots.length - 1)) * 100},${88 - ((snapshot.overall - min) / range) * 64}`).join(" ");
  return <svg className="trend-chart" viewBox="0 0 100 100" role="img" aria-label={`Familiarity trend from ${percent(snapshots[0].overall)} percent to ${percent(snapshots[snapshots.length - 1].overall)} percent`} preserveAspectRatio="none"><line x1="0" y1="88" x2="100" y2="88" className="chart-axis" /><polyline points={points} className="trend-line" vectorEffect="non-scaling-stroke" fill="none" /></svg>;
}

interface ProgressPageProps {
  state: PersistedStateV1;
  tab: ProgressTab;
  onTab: (tab: ProgressTab) => void;
  onExport: () => void;
  onReset: () => void;
  confirmReset: boolean;
  onConfirmReset: () => void;
  onCancelReset: () => void;
}

function ProgressPage({ state, tab, onTab, onExport, onReset, confirmReset, onConfirmReset, onCancelReset }: ProgressPageProps): VNode {
  const seen = catalog.filter((set) => (state.familiarity[set.id]?.seen ?? 0) > 0).length;
  return <div className="content-column progress-page-content"><div className="page-title"><div><p className="eyebrow">Keep learning</p><h1>Progress</h1><p>Familiarity is tracked separately for every set and answer field.</p></div><div className="page-actions"><button className="button button-secondary" type="button" onClick={onExport}>Export progress</button><button className="button button-quiet" type="button" onClick={onReset}>Reset</button></div></div><FamiliarityCard state={state} tab={tab} onTab={onTab} large /><div className="progress-stat-strip"><LabeledValue label="Overall score" value={`${percent(overallMean(catalog, state.familiarity))}%`} /><LabeledValue label="Sets seen" value={`${seen} / ${catalog.length}`} /><LabeledValue label="Answers" value={String(state.history.length)} /><LabeledValue label="Accuracy" value={`${percent(historyAccuracy(state))}%`} /></div><section className="card progress-history"><div className="panel-header"><div><p className="eyebrow">Saved locally</p><h2>Answer history</h2></div></div>{state.history.length ? <HistoryTable entries={state.history} /> : <p className="empty-copy">No answers yet. Your first round will start the history.</p>}</section>{confirmReset && <ResetConfirm onConfirm={onConfirmReset} onCancel={onCancelReset} />}</div>;
}

function ResetConfirm({ onConfirm, onCancel }: { onConfirm: () => void; onCancel: () => void }): VNode {
  return <div className="reset-confirm" role="alert"><strong>Reset all local progress?</strong><p>This removes familiarity, history, score, streak, and saved settings from this browser.</p><div><button className="button button-danger" type="button" onClick={onConfirm}>Reset everything</button><button className="button button-quiet" type="button" onClick={onCancel}>Cancel</button></div></div>;
}

function AboutPage(): VNode {
  return <div className="content-column about-page-content">
    <div className="page-title"><div><p className="eyebrow">A small study game</p><h1>About Guess That Set</h1><p>Learn to recognize Pokémon TCG set symbols, names, eras, and release years.</p></div></div>
    <div className="about-grid">
      <section className="card about-card">
        <div className="panel-header"><div><p className="eyebrow">The game</p><h2>Learn one symbol at a time</h2></div></div>
        <p>Guess That Set shows a set symbol and asks you to identify the set details you choose. Every answer gives immediate feedback, while your familiarity is saved locally so less familiar sets can return more often.</p>
        <p className="muted-copy">The game is client-side and does not require an account. Your progress stays in this browser unless you export it.</p>
      </section>
      <section className="card about-card">
        <div className="panel-header"><div><p className="eyebrow">Open-source data</p><h2>Used repositories</h2></div></div>
        <ul className="repository-list">
          <li><a className="repository-link" href="https://github.com/SilphGhost/pokemon-tcg-symbols" target="_blank" rel="noreferrer"><span>pokemon-tcg-symbols</span><span aria-hidden="true">↗</span></a><p>Set-symbol artwork in PNG and SVG formats.</p></li>
          <li><a className="repository-link" href="https://github.com/PokemonTCG/pokemon-tcg-data" target="_blank" rel="noreferrer"><span>pokemon-tcg-data</span><span aria-hidden="true">↗</span></a><p>Canonical Pokémon TCG set metadata used by the catalog.</p></li>
        </ul>
      </section>
    </div>
    <section className="card about-disclaimer">
      <div className="panel-header"><div><p className="eyebrow">Please note</p><h2>Unofficial educational project</h2></div></div>
      <p>Guess That Set is a fan-made project and is not affiliated with, endorsed by, or sponsored by The Pokémon Company International. Pokémon, Pokémon TCG, set names, and set symbols belong to their respective rights holders.</p>
      <p className="muted-copy">The symbols are used here for educational and non-commercial purposes. Review the notices in the upstream repositories and applicable rights before redistributing or deploying this project.</p>
    </section>
  </div>;
}

interface SettingsPageProps {
  state: PersistedStateV1;
  draftSettings: GameSettings;
  scopeSearch: string;
  onScopeSearch: (value: string) => void;
  onDraft: (mutator: (settings: GameSettings) => void) => void;
  onStart: () => void;
  onNotice?: (message: string) => void;
  onAppearance: (appearance: "system" | "light" | "dark") => void;
  onExport: () => void;
  onReset: () => void;
  confirmReset: boolean;
  onConfirmReset: () => void;
  onCancelReset: () => void;
}

function SettingsPage({ state, draftSettings, scopeSearch, onScopeSearch, onDraft, onStart, onNotice, onAppearance, onExport, onReset, confirmReset, onConfirmReset, onCancelReset }: SettingsPageProps): VNode {
  return <div className="content-column settings-page-content"><div className="page-title"><div><p className="eyebrow">Configure your game</p><h1>Config</h1><p>Quiz changes are saved when you start a new game.</p></div></div><SettingsCard draftSettings={draftSettings} candidateCount={getScopeCandidates(draftSettings.scope, catalog).length} scopeSearch={scopeSearch} onScopeSearch={onScopeSearch} onDraft={onDraft} onStart={onStart} onNotice={onNotice} /><section className="card appearance-card"><div className="panel-header"><div><p className="eyebrow">Display</p><h2>Appearance</h2></div></div><div className="appearance-options">{(["system", "light", "dark"] as const).map((appearance) => <label className={`appearance-option${state.settings.appearance === appearance ? " is-selected" : ""}`} key={appearance}><input type="radio" name="appearance" checked={state.settings.appearance === appearance} onChange={() => onAppearance(appearance)} />{appearance[0].toLocaleUpperCase() + appearance.slice(1)}{appearance === "system" && <small>Follow your device</small>}</label>)}</div></section><section className="card local-data-card"><div className="panel-header"><div><p className="eyebrow">This browser</p><h2>Local data</h2></div></div><p>Your score, config, familiarity, and latest 100 answers stay on this device. Export a JSON backup before clearing them.</p><div className="data-actions"><button className="button button-secondary" type="button" onClick={onExport}>Export progress</button><button className="button button-danger-outline" type="button" onClick={onReset}>Reset local data</button></div>{confirmReset && <ResetConfirm onConfirm={onConfirmReset} onCancel={onCancelReset} />}</section></div>;
}

interface SetsPageProps {
  state: PersistedStateV1;
  search: string;
  eraFilter: string;
  onSearch: (value: string) => void;
  onEra: (value: string) => void;
  onPracticeEra: (eraId: string) => void;
}

function SetsPage({ state, search, eraFilter, onSearch, onEra, onPracticeEra }: SetsPageProps): VNode {
  const detailId = getQuery().get("set");
  const detail = detailId ? catalogById.get(detailId) : undefined;
  const matches = rankMatchesByFields(catalog, search, (set) => [set.name, set.id, set.era, String(set.year)]).filter(({ item }) => !eraFilter || item.eraId === eraFilter);
  const groups = eraOptions.map((era) => ({ era, sets: matches.filter(({ item }) => item.eraId === era.id).map(({ item }) => item) })).filter(({ sets }) => sets.length > 0);
  return <div className="content-column sets-page-content"><div className="page-title"><div><p className="eyebrow">Learn outside a round</p><h1>Set catalog</h1><p>Browse sets by era and practice a whole era without narrowing the name suggestions.</p></div></div>{detail && <section className="card set-detail"><div className="set-detail-symbol"><SymbolImage set={detail} asking={false} className="catalog-symbol" /></div><div><p className="eyebrow">Set detail</p><h2>{detail.name}</h2><p>{detail.era} · {displayDate(detail)}</p><div className="detail-familiarity">{FIELDS.map((field) => <span key={field}>{fieldLabel(field)} <strong>{percent(state.familiarity[detail.id]?.[field] ?? 0)}%</strong></span>)}</div></div></section>}<section className="card catalog-card"><div className="catalog-controls"><label className="search-field"><span>Search sets</span><input type="search" autoComplete="off" autoCorrect="off" autoCapitalize="none" spellcheck={false} value={search} placeholder="Search name, ID, era, or year" onInput={(event) => onSearch((event.currentTarget as HTMLInputElement).value)} /></label><label className="search-field"><span>Filter by era</span><select value={eraFilter} onChange={(event) => onEra((event.currentTarget as HTMLSelectElement).value)}><option value="">All eras</option>{eraOptions.map((era) => <option value={era.id} key={era.id}>{era.name}</option>)}</select></label><span className="catalog-count">{matches.length} of {catalog.length} sets</span></div>{matches.length === 0 ? <p className="empty-copy">No sets match that search. Try a name, ID, year, or era.</p> : <div className="era-groups">{groups.map(({ era, sets }) => <section className="era-group" key={era.id} aria-labelledby={`era-heading-${era.id}`}><div className="era-group-header"><div><p className="eyebrow">Era</p><h2 id={`era-heading-${era.id}`}>{era.name}</h2><p className="era-group-count">{sets.length} matching of {era.count} sets</p></div><button className="button button-secondary" type="button" onClick={() => onPracticeEra(era.id)}>Practice this era</button></div><div className="catalog-grid">{sets.map((set) => <SetCard key={set.id} set={set} familiarity={state.familiarity[set.id]} />)}</div></section>)}</div>}</section></div>;
}

function SetCard({ set, familiarity }: { set: SetRecord; familiarity?: PersistedStateV1["familiarity"][string] }): VNode {
  return <article className="set-card"><div className="catalog-symbol-wrap"><SymbolImage set={set} asking={false} className="catalog-symbol" /></div><div className="set-card-copy"><h2>{set.name}</h2><p>{set.era} · {displayDate(set)}</p><div className="set-metadata"><code>{set.id}</code><span>{set.year}</span></div><div className="set-familiarity">{FIELDS.map((field) => <span key={field}><small>{field === "name" ? "Name" : field === "era" ? "Era" : "Year"}</small><strong>{percent(familiarity?.[field] ?? 0)}%</strong></span>)}</div></div></article>;
}

render(<App />, app);
